"use client";
import { Box } from "@ecco/ui/box";
import {
    LOAD_REVIEWS_EVENT,
    REVIEWS_SECTION_ID,
} from "@components/commerce/reviews/yotpo/yotpo-reviews-widget";
import { Text } from "@ecco/ui/text";
import { CommonCMS } from "@lib/constants/contentful";
import StarsRating from "@ecco/ui/stars-rating";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { useEffect, useState } from "react";
import { useProductReviews } from "@components/commerce/reviews/yotpo/getProductReviews";
import { useParams } from "next/navigation";
import { Button } from "@ecco/ui/button";
import { styles } from "./styles";

const WRITE_REVIEW_BUTTON_SELECTOR = "#yotpo-main-widget-btn";
const REVIEW_FORM_SELECTOR = ".yotpo-review-form";
const WRITE_REVIEW_TIMEOUT_MS = 8000;
const WRITE_REVIEW_RETRY_MS = 250;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Yotpo now loads on demand and renders its button before binding a click handler,
// so the click is retried until the review form actually opens.
const openWriteReviewForm = async () => {
    const deadline = Date.now() + WRITE_REVIEW_TIMEOUT_MS;

    while (Date.now() < deadline) {
        if (document.querySelector(REVIEW_FORM_SELECTOR)) return;

        document.querySelector<HTMLButtonElement>(WRITE_REVIEW_BUTTON_SELECTOR)?.click();
        await wait(WRITE_REVIEW_RETRY_MS);
    }
};

export const ProductBuyCardReviews = ({ productKey }: { productKey: string }) => {
    const { get: getMicrocopy } = useMicrocopy();

    const { articleNumber } = useParams();
    const productReviews = useProductReviews(articleNumber as string);

    const [averageReviewScore, setAverageScore] = useState(productReviews?.averageScore);
    const [totalReviews, setTotalReviews] = useState(productReviews?.totalScore);

    useEffect(() => {
        setAverageScore(productReviews?.averageScore);
        setTotalReviews(productReviews?.totalScore);

        const targetNode = document?.querySelector(`#${REVIEWS_SECTION_ID}`);
        let observer: MutationObserver | null = null;

        if (targetNode) {
            observer = new MutationObserver((mutationsList) => {
                for (const mutation of mutationsList) {
                    if (mutation.type === "childList") {
                        const scoreElement = document.querySelector(".yotpo-bottom-line-score");
                        if (scoreElement) {
                            const scoreText = scoreElement.textContent;
                            const scoreMatch = scoreText.match(/\d+(\.\d+)?/);
                            if (scoreMatch) {
                                setAverageScore(parseFloat(scoreMatch[0]));
                            }
                        }

                        const reviewCountElement = document.querySelector(
                            ".yotpo-bottom-line-basic-text"
                        );
                        if (reviewCountElement) {
                            const textContent = reviewCountElement.textContent;
                            const numberMatch = textContent.match(/\d+/);
                            if (numberMatch) {
                                setTotalReviews(parseInt(numberMatch[0], 10));
                            }
                        }
                        observer?.disconnect();
                    }
                }
            });

            const config = { childList: true, subtree: true };
            observer.observe(targetNode, config);
        }

        return () => {
            observer?.disconnect();
        };
    }, [productKey]);

    const activateReviews = async () => {
        const reviewSection = document.querySelector(`#${REVIEWS_SECTION_ID}`);
        if (!reviewSection) return;

        reviewSection.dispatchEvent(new CustomEvent(LOAD_REVIEWS_EVENT));
        reviewSection.scrollIntoView({ behavior: "smooth" });

        if (!totalReviews) {
            await openWriteReviewForm();
        }
    };

    return (
        <Box>
            <Button
                {...styles.reviewsTriggerButton}
                onClick={activateReviews}
                aria-label={getMicrocopy(CommonCMS.page, CommonCMS.PDP.reviews)}
            >
                <Box display="flex" gap={1}>
                    <StarsRating score={averageReviewScore} />{" "}
                    <Text as="span" textAlign="left" fontSize="mobileBodyTextSmall">
                        ({totalReviews})
                    </Text>
                </Box>
                {!totalReviews && (
                    <Text
                        as="span"
                        textAlign="left"
                        fontSize="mobileBodyTextTiny"
                        textDecor="underline"
                    >
                        {getMicrocopy(CommonCMS.page, CommonCMS.PDP.writeFirstReview)}
                    </Text>
                )}
            </Button>
        </Box>
    );
};

export default ProductBuyCardReviews;
